import { useCallback, useMemo } from 'react';
import type { Phase, TimelineEvent } from 'src/types/sentinel';
import { useAppSelector } from 'universal-scripts';

export function useSentinelPhase() {
	const phases = useAppSelector((state) => state.sentinel.phases);
	const currentPhase = useAppSelector((state) => state.sentinel.currentPhase);
	const timeline = useAppSelector((state) => state.sentinel.timeline);

	const sessionTimeline = useMemo<TimelineEvent[]>(() => {
		const allEvents = Object.values(timeline).sort((a, b) => a.timestamp - b.timestamp);

		let lastStartIndex = 0;
		for (let i = 0; i < allEvents.length; i++) {
			const e = allEvents[i];
			if (e.phase === 'detect' && e.eventType === 'phase_start') {
				lastStartIndex = i;
			}
		}

		return allEvents.slice(lastStartIndex);
	}, [timeline]);

	const activeSteps = useMemo<TimelineEvent[]>(() => {
		return sessionTimeline.filter((event) => event.eventType === 'step_update' && event.stepStatus === 'in_progress');
	}, [sessionTimeline]);

	const isPhaseRunning = useCallback((phase: Phase): boolean => phases[phase].status === 'processing', [phases]);

	const isPhaseCompleted = useCallback((phase: Phase): boolean => phases[phase].status === 'completed', [phases]);

	const isPhaseActive = useCallback(
		(phase: Phase): boolean => currentPhase === phase && phases[phase].status === 'processing',
		[currentPhase, phases]
	);

	const isProcessFinished = useCallback((): boolean => {
		return phases.respond.status === 'completed';
	}, [phases]);

	const getPhaseClassName = useCallback(
		(phase: Phase | string | null | undefined): string => {
			if (!phase || !phases[phase as Phase]) return '';
			const p = phase as Phase;
			if (currentPhase === p && phases[p].status === 'processing') return 'active';
			if (phases[p].status === 'completed') return 'completed';
			if (phases[p].status === 'error') return 'error';
			return '';
		},
		[currentPhase, phases]
	);

	return {
		currentPhase,
		phases,
		timeline,
		sessionTimeline,
		activeSteps,
		isPhaseRunning,
		isPhaseCompleted,
		isPhaseActive,
		isProcessFinished,
		getPhaseClassName
	};
}
